import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { tokenStore } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data?.access_token) tokenStore.set(data.access_token);
        setUser(data);
        return data;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        if (data?.access_token) tokenStore.set(data.access_token);
        setUser(data);
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        tokenStore.clear();
        setUser(false);
    };

    const hasPermission = useCallback((permission) => {
        if (!permission) return true;
        if (user?.role === "superadmin") return true;
        return Boolean(user && (user.permissions || []).includes(permission));
    }, [user]);

    const hasAnyPermission = useCallback((...permissions) => {
        const flat = permissions.flat().filter(Boolean);
        if (!flat.length) return true;
        return flat.some((permission) => hasPermission(permission));
    }, [hasPermission]);

    return (
        <AuthContext.Provider value={{
            user, loading, login, register, logout, refresh: checkAuth, setUser,
            hasPermission, hasAnyPermission,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
